import { Glue42 } from "../../glue";
import { Glue42Core } from "@glue42/core";
import CallbackFactory, { CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import CaptureOptions = Glue42.Displays.CaptureOptions;

const T42DisplayCommand = "T42.Displays.Command";
const T42DisplayOnEvent = "T42.Displays.OnEvent";

export default class DisplayManager implements Glue42.Displays.API {
    private _registry: CallbackRegistry = CallbackFactory();
    private _registered = false;

    constructor(
        private _agm: Glue42Core.AGM.API,
        private _logger: Glue42Core.Logger.API,
    ) {
        //
    }

    public all = async (): Promise<Glue42.Displays.Display[]> => {
        const displays = await this.callGD<Glue42.Displays.Display[]>(DisplayCommand.GetAll, {});
        return displays.map(this.decorateDisplay);
    }

    public get = async (id: number): Promise<Glue42.Displays.Display> => {
        const display = await this.callGD<Glue42.Displays.Display>(DisplayCommand.Get, { id });
        return this.decorateDisplay(display);
    }

    public getPrimary = async (): Promise<Glue42.Displays.Display> => {
        const primary = (await this.all()).find((d) => d.isPrimary);
        return primary;
    }

    public getByWindowId(id: string): Promise<Glue42.Displays.Display> {
        const current = this.callGD<Glue42.Displays.Display>(DisplayCommand.GetByWindowId, { id });
        return current;
    }

    public capture = async (options: CaptureOptions): Promise<string> => {
        const screenshot = await this.callGD<string>(DisplayCommand.Capture, { ...options });
        return screenshot;
    }

    public captureAll = async (options: Glue42.Displays.CaptureAllOptions): Promise<string | string[]> => {
        const screenshots = await this.callGD<string | string[]>(DisplayCommand.CaptureAll, { ...options });
        return screenshots;
    }

    public getMousePosition = async (): Promise<Glue42.Displays.Point> => {
        const point = await this.callGD<Glue42.Displays.Point>(DisplayCommand.GetMousePosition);
        return point;
    }

    public onDisplayChanged(cb: (displays: Glue42.Displays.Display[]) => void) {
        this.register();
        return this._registry.add("on-display-changed", cb);
    }

    private callGD = async <T>(command: string, options?: object): Promise<T> => {
        // Consider targeting only GD
        const invocationResult = await this._agm.invoke(T42DisplayCommand, { options: { ...options }, command });
        return invocationResult.returned.data;
    }

    private decorateDisplay = (original: Glue42.Displays.Display): Glue42.Displays.Display => {
        const decoratedDisplay = {
            ...original,
            capture: (size: SizeOptions) => this.capture({ id: original.id, size })
        };
        const workAreaAsAny = decoratedDisplay.workArea as any;
        workAreaAsAny.x = workAreaAsAny.left;
        workAreaAsAny.y = decoratedDisplay.workArea.top;
        return decoratedDisplay;
    }

    private register() {
        if (this._registered) {
            return;
        }
        this._registered = true;
        this._agm.register<{ event: string, data: any }>(T42DisplayOnEvent, (args, caller) => {
            const event = args.event;
            const data = args.data;
            switch (event) {
                case "display-changed":
                    this._registry.execute("on-display-changed", data.displays.map(this.decorateDisplay));
                    break;
                default:
                    this._logger.warn(`unknown event - ${event}`);
                    break;
            }
        });
    }
}

type SizeOptions = Glue42.Displays.ScaleOptions | Glue42.Displays.AbsoluteSizeOptions;

enum DisplayCommand {
    Capture = "capture",
    CaptureAll = "captureAll",
    GetAll = "getAll",
    Get = "get",
    GetByWindowId = "getByWindowId",
    GetMousePosition = "getMousePosition",
}
